import React, { createContext, useContext, useEffect, useState, useMemo } from 'react';
import { getAllTags } from '../api/apiClient';

const LS_KEY = 'explora_tags_cache_v1';

const TagsContext = createContext({
  tags: [],
  loading: true,
  resolveTagSlug: (tagName) => null,
});

const readCache = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeCache = (tags) => {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(tags));
  } catch {
    /* quota / private mode */
  }
};

/**
 * Loads all tags once (with localStorage cache for instant availability) and
 * exposes a canonical slug resolver.
 *
 * resolveTagSlug(tagName) returns the canonical slug (without `/photos-hd/`)
 * derived from the DB's `tag.slug` field. If the tag isn't found in the loaded
 * list (e.g. first visit before fetch completes), it returns an empty string —
 * the caller should hide the link rather than render a wrong URL.
 */
export const TagsProvider = ({ children }) => {
  // Seed from cache so resolveTagSlug works on first render
  const [tags, setTags] = useState(() => readCache());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await getAllTags();
        const list = Array.isArray(data) ? data : (data?.tags || []);
        if (!cancelled && list.length) {
          setTags(list);
          writeCache(list);
        }
      } catch (err) {
        console.error('Failed to load tags:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    // Defer off the critical path so /api/tags never competes with LCP
    // resources on first paint. Seeded localStorage cache keeps slug
    // resolution working immediately on repeat visits.
    const ric = window.requestIdleCallback || ((cb) => setTimeout(cb, 1200));
    const handle = ric(load, { timeout: 3000 });
    return () => {
      cancelled = true;
      if (window.cancelIdleCallback && typeof handle === 'number') {
        window.cancelIdleCallback(handle);
      }
    };
  }, []);

  const byName = useMemo(() => {
    const map = new Map();
    for (const t of tags) {
      if (t?.cat_name && t?.slug) {
        map.set(t.cat_name.trim().toLowerCase(), t.slug);
      }
    }
    return map;
  }, [tags]);

  const resolveTagSlug = (tagName) => {
    if (!tagName) return '';
    const key = String(tagName).trim().toLowerCase();
    const canonical = byName.get(key);
    if (canonical) {
      return canonical.replace(/^\/photos-hd\//, '');
    }
    // No canonical mapping yet — return empty so callers can skip rendering
    // a broken link instead of producing a wrong plural slug.
    return '';
  };

  return (
    <TagsContext.Provider value={{ tags, loading, resolveTagSlug }}>
      {children}
    </TagsContext.Provider>
  );
};

export const useTags = () => useContext(TagsContext);
